import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify'
import { UserInfoService } from '@eureka/domains/src/user-info'
import { HomeLsApi } from '@/domains/home/apis/home-ls-api'
import type { ScenarioType } from '@/types/pda'
import type { UploadResponse } from '@/types/upload'
import type { AgentUploadConfig, AgentUploadParams, AgentUploadStrategy } from './agent-upload-strategy'
import { BaseAgentUploadStrategy } from './base-agent-upload-strategy'

type PdaAgentUploadParams = {
    scenario: ScenarioType
}

@injectable()
export class PdaAgentUploadStrategy implements AgentUploadStrategy<PdaAgentUploadParams> {
    constructor(
        @inject(BaseAgentUploadStrategy) private _baseAgentUploadStrategy: BaseAgentUploadStrategy,
        @inject(HomeLsApi) private _homeLsApi: HomeLsApi,
        @inject(UserInfoService) private _userInfoService: UserInfoService
    ) {}

    async getLimit(): Promise<number> {
        const [isEnterpriseCommercial, isTrial, isFreeUser, isPro] = await Promise.all([
            this._userInfoService.getIsEnterpriseCommercialUser(),
            this._userInfoService.getIsTrialUser(),
            this._userInfoService.getIsFreeUser(),
            this._userInfoService.getIsPro()
        ])
        if (isFreeUser) return 2
        if (isPro) return 5
        if (isEnterpriseCommercial || isTrial) return 10

        return 2
    }

    getUploadConfig(): AgentUploadConfig {
        return {
            sizeLimit: 50 * 1024 * 1024,
            accept: '.pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png',
        }
    }

    async upload(
        params: AgentUploadParams,
        agentParams?: PdaAgentUploadParams
    ): Promise<UploadResponse | undefined> {
        const normalizedParams = this._baseAgentUploadStrategy.normalizeParams(params)

        return this._homeLsApi.pdaUpload(
            { file: normalizedParams.file, file_scenario: agentParams?.scenario },
            normalizedParams.onProgress,
            normalizedParams.signal
        )
    }
}
